import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography } from '@mui/material'
import { TextField } from '@carsys/ui'
import { LoadingButton } from '@mui/lab'
import { POSTSeedAccountancyDataResponse, POSTSeedDataResponse } from '@cbgms/api'
import { ProposalSource, Roles, ShopStatus } from '@cbgms/api/modules/enums'
import { enumToOptions } from '@cbgms/base/utils/helpers'
import { isRequired, isValidEmail, isValidPhone, isValidURL } from '@cbgms/base/utils/validations'
import Dropdown from '@cbgms/components/form/Material/Dropdown'
import Input from '@cbgms/components/form/Material/Input'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Feature } from '@cbgms/base/feature-flags'
import { useBlobDownloader } from '@cbgms/base/hooks/use-blob-downloader'
import { useNotifier } from '@cbgms/base/notifier'
import moment from 'moment'
import { getVinRequestLogExport, seedAccountancyDataForShop, seedDataForShop } from '../state/actions'
import {
  DefaultSeedAccountancyDataCreateInput,
  DefaultSeedDataCreateInput
} from 'app-support/shops/components/SeedData/DefaultSeedDataCreateInput'
import StartConversionForm from './StartConversionForm'
import HasConversionPermission from 'app-support/conversion/HasConversionPermission'
import { CopyShopDataForm } from './CopyShopDataForm'
import { PhoneNumberInputRedux } from '@cbgms/components/form/PhoneNumberInput'
import { CountryCode } from '@carsys/enums/country-code'
import { IRootReducerState } from 'state/reducers'

export interface IGeneralProps {
  takeoverURL?: string
  shopID?: string
  companyUUID?: string
  country?: CountryCode
}

function hasShopIdAndCompanyUUID(input: IGeneralProps): input is { shopID: string; companyUUID: string } {
  return !!input.shopID && !!input.companyUUID
}

function validateDatesPure(from: string, to: string, t: (key: string) => string): { valid: boolean; error: string } {
  if (!from || !to) return { valid: false, error: '' }

  const fromDt = moment(from)
  const toDt = moment(to)

  if (!fromDt.isValid() || !toDt.isValid()) {
    return { valid: false, error: t('vin.validation.invalid_date_format') }
  }

  if (fromDt.isAfter(toDt)) {
    return { valid: false, error: t('vin.validation.from_greater_than_to') }
  }

  const fromPlus3Months = moment(fromDt).add(3, 'months')

  if (toDt.isAfter(fromPlus3Months)) {
    return { valid: false, error: t('vin.validation.date_gap_exceeds_limit') }
  }

  return { valid: true, error: '' }
}

export const General: React.FC<IGeneralProps> = props => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const role = useSelector<IRootReducerState, Roles>(state => state.auth.user['custom:role'])
  const download = useBlobDownloader()
  const notifier = useNotifier()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isLoadingAccountancy, setIsLoadingAccountancy] = React.useState(false)
  const [isDownloadingVinReport, setIsDownloadingVinReport] = React.useState(false)
  const [seedDataOutput, setSeedDataOutput] = React.useState<POSTSeedDataResponse['Data']>()
  const [seedAccountancyDataOutput, setSeedAccountancyDataOutput] = React.useState<POSTSeedAccountancyDataResponse['Data']>()
  const [seedDataInput, setSeedDataInput] = React.useState(DefaultSeedDataCreateInput)
  const [seedAccountancyDataInput, setSeedAccountancyDataInput] = React.useState(DefaultSeedAccountancyDataCreateInput)
  const seedData = async () => {
    if (hasShopIdAndCompanyUUID(props)) {
      setIsLoading(true)
      try {
        const { Data } = await dispatch(
          seedDataForShop({
            ShopID: props.shopID!,
            CompanyUUID: props.companyUUID!,
            ...seedDataInput
          })
        )
        setSeedDataOutput(Data)
      } catch (error: any) {
        setSeedDataOutput(error)
      }
      setIsLoading(false)
    }
  }

  const seedAccountancyData = async () => {
    if (hasShopIdAndCompanyUUID(props)) {
      setIsLoadingAccountancy(true)
      try {
        const { Data } = await dispatch(
          seedAccountancyDataForShop({
            ShopID: props.shopID!,
            CompanyUUID: props.companyUUID!,
            ...seedAccountancyDataInput
          })
        )
        setSeedAccountancyDataOutput(Data)
      } catch (error: any) {
        setSeedAccountancyDataOutput(error)
      }
      setIsLoadingAccountancy(false)
    }
  }

  const sourceOptions = enumToOptions(ProposalSource, 'EnumProposalSource')
  const shopStatusOptions = enumToOptions(ShopStatus, 'EnumShopStatus')

  const currentDate = moment().format('YYYY-MM-DD')
  const [fromDate, setFromDate] = useState<string>(currentDate)
  const [toDate, setToDate] = useState<string>(currentDate)
  const [error, setError] = useState<string>('')
  const [errorField, setErrorField] = useState<'from' | 'to' | null>(null)

  const { valid: isValid, error: currentError } = useMemo(() => validateDatesPure(fromDate, toDate, t), [fromDate, toDate, t])

  useEffect(() => {
    setError(currentError)

    if (!currentError) {
      setErrorField(null)
    }
  }, [currentError])

  const canGenerateVinReport = Boolean(fromDate && toDate && isValid)

  const handleGenerateVinReport = async () => {
    if (!props.shopID || !canGenerateVinReport) return

    setIsDownloadingVinReport(true)
    try {
      const blob = await dispatch(
        getVinRequestLogExport({
          'start-date': fromDate,
          'end-date': toDate,
          'shop-id': props.shopID
        })
      )

      const filename = `VIN-Report-${props.shopID}-${fromDate}-to-${toDate}.csv`
      download(blob, filename)
      notifier.success(t('vin.report_downloaded_successfully'))
    } catch (error: any) {
      notifier.error(error?.message || t('vin.report_download_failed'))
    } finally {
      setIsDownloadingVinReport(false)
    }
  }

  return (
    <Grid spacing={2} container>
      <Grid container item spacing={1}>
        <Grid item xs={12}>
          <Typography variant='h5'>{t('shop.general_segment_header')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Input label={t('shop.shop_name')} name='Shop.Name' inputProps={{ maxLength: 100 }} required validate={isRequired} fullWidth />
        </Grid>
        <Grid item xs={4}>
          <Dropdown label={t('shop.authorized_for_source')} multiple name='Shop.AuthorizedForSources' options={sourceOptions} fullWidth />
        </Grid>
        <Grid item xs={4}>
          <Dropdown fullWidth required label={t('shop.status')} name='Shop.Status' validate={isRequired} options={shopStatusOptions} />
        </Grid>
      </Grid>

      <Grid container item spacing={1}>
        <Grid item xs={12}>
          <Typography variant='h5'>{t('shop.contact_segment_header')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Input
            label={t('shop.company_email_address')}
            inputProps={{ maxLength: 100 }}
            name='Shop.EmailAddress'
            required
            validate={[isRequired, isValidEmail]}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <PhoneNumberInputRedux
            defaultCountry={props.country || CountryCode.NL}
            labelTranslationKey='shop.phone_number'
            required
            name='Shop.PhoneNumber'
            validator={isValidPhone}
            validate={isRequired}
            inputProps={{ maxLength: 20 }}
          />
        </Grid>
        <Grid item xs={4}>
          <Input label={t('shop.website_url')} inputProps={{ maxLength: 100 }} name='Shop.WebsiteURL' validate={isValidURL} fullWidth />
        </Grid>
        <Grid item xs={4}>
          <Input
            label={t('shop.terms_url')}
            inputProps={{ maxLength: 100 }}
            name='Shop.TermsAndConditionsURL'
            validate={isValidURL}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <Input
            label={t('shop.privacy_url')}
            inputProps={{ maxLength: 100 }}
            name='Shop.PrivacyStatementURL'
            validate={isValidURL}
            fullWidth
          />
        </Grid>
      </Grid>

      <Grid container item spacing={1}>
        <Grid item xs={12}>
          <Typography variant='h5'>{t('shop.external_gms_segment_header')}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Input name='ExternalGMS.Name' label={t('shop.external_gms')} inputProps={{ maxLength: 100 }} fullWidth disabled />
        </Grid>
      </Grid>

      <Grid container item spacing={1}>
        <Grid item xs={12}>
          <Typography variant='h5'>{t('shop.takeover_session_segment_header')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            data-testid='general-takeover-button'
            href={props.takeoverURL}
            component='a'
            variant='outlined'
            disabled={!props.takeoverURL}
            target='_blank'
          >
            {t('shop.takeover_session_button')}
          </Button>
        </Grid>
      </Grid>

      {hasShopIdAndCompanyUUID(props) && (
        <Grid container item spacing={1}>
          <Grid item xs={12}>
            <Typography variant='h5'>{t('shop.seed_data_segment_header')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              data-testid='shop-seed-working-data-button'
              variant='outlined'
              onClick={() => seedData()}
              disabled={isLoading}
              loading={isLoading}
            >
              {t('shop.seed_working_data_button')}
            </LoadingButton>
          </Grid>
          <Grid item xs={12}>
            <Accordion elevation={2}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t('shop.changeSeedDateInput')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  multiline
                  minRows={Object.keys(seedDataInput).length + 2}
                  defaultValue={JSON.stringify(seedDataInput, null, 1)}
                  // Need other refactor to prevent console errors or show user JSON parse error...
                  onChange={v => setSeedDataInput(JSON.parse(v.target.value?.toString() || ''))}
                />
              </AccordionDetails>
            </Accordion>
            {seedDataOutput && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Output</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre data-testid='shop-seed-working-data-output'>{JSON.stringify(seedDataOutput, null, 2)}</pre>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
        </Grid>
      )}
      <HasConversionPermission>
        {hasShopIdAndCompanyUUID(props) && <StartConversionForm shopID={props.shopID} companyUUID={props.companyUUID} />}
      </HasConversionPermission>
      {hasShopIdAndCompanyUUID(props) && <CopyShopDataForm shopID={props.shopID} companyUUID={props.companyUUID} />}
      {hasShopIdAndCompanyUUID(props) && (
        <Grid container item spacing={1}>
          <Grid item xs={12}>
            <Typography variant='h5'>{t('shop.seed_accountancy_segment_header')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <LoadingButton
              data-testid='shop-seed-accountancy-data-button'
              onClick={() => seedAccountancyData()}
              variant='outlined'
              disabled={isLoadingAccountancy}
              loading={isLoadingAccountancy}
            >
              {t('shop.seed_working_data_button')}
            </LoadingButton>
          </Grid>

          <Grid item xs={12}>
            <Accordion elevation={2}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t('shop.changeSeedDateInput')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  multiline
                  minRows={Object.keys(seedAccountancyDataInput).length + 2}
                  defaultValue={JSON.stringify(seedAccountancyDataInput, null, 1)}
                  // Need other refactor to prevent console errors or show user JSON parse error...
                  onChange={v => setSeedAccountancyDataInput(JSON.parse(v.target.value?.toString() || ''))}
                />
              </AccordionDetails>
            </Accordion>
            {seedDataOutput && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Output</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre data-testid='shop-seed-working-accountancy-output'>{JSON.stringify(seedAccountancyDataOutput, null, 2)}</pre>
                </AccordionDetails>
              </Accordion>
            )}
          </Grid>
        </Grid>
      )}
      <Feature feature='uk-vin-masking'>
        {role === Roles.Admin && (
          <Grid container item spacing={1}>
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <Typography variant='h5'>{t('vin.audit_logs')}</Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                type='date'
                label={t('vin.from_date.label')}
                value={fromDate}
                onChange={e => {
                  setFromDate(e.target.value)
                  setErrorField('from')
                }}
                InputLabelProps={{ shrink: true }}
                placeholder={t('vin.from_date.label')}
                fullWidth
                error={Boolean(error) && errorField === 'from'}
                helperText={(errorField === 'from' && error) || ' '}
                sx={{
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                type='date'
                label={t('vin.to_date.label')}
                value={toDate}
                onChange={e => {
                  setToDate(e.target.value)
                  setErrorField('to')
                }}
                InputLabelProps={{ shrink: true }}
                placeholder={t('vin.to_date.label')}
                fullWidth
                error={Boolean(error) && errorField === 'to'}
                helperText={(errorField === 'to' && error) || ' '}
                inputProps={{
                  max: currentDate
                }}
                sx={{
                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <LoadingButton
                variant='contained'
                onClick={handleGenerateVinReport}
                disabled={!canGenerateVinReport}
                loading={isDownloadingVinReport}
                sx={{
                  cursor: 'pointer !important'
                }}
              >
                {t('vin.generate_report')}
              </LoadingButton>
            </Grid>
          </Grid>
        )}
      </Feature>
    </Grid>
  )
}

export default General
